/**
 * First-pass deep search service for top navigation.
 * It reuses existing page services and normalizes matches into one lightweight shape.
 */
import { Injectable } from '@angular/core';
import { DocumentRegisterService } from '@app/core/services/document-register.service';
import { UploadResultsService } from '@modules/invoice-management/services/upload-results.service';
import { GetSessionService } from '@modules/reconciliation-manager/services/get-session.service';
import {
    TopNavDeepSearchMatchedField,
    TopNavDeepSearchResult,
} from '@modules/navigation/models/top-nav-deep-search.model';
import { ROUTE_ROOTS } from '@modules/navigation/models';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

interface SearchableDocumentRow {
    id?: string | number;
    docId?: string | number;
    documentId?: string | number;
    supplierName?: string;
    vendorName?: string;
    runId?: string;
    runIdShort?: string;
    status?: string;
    documentType?: string;
    type?: string;
}

interface SearchableSessionRow {
    id?: string;
    status?: string;
}

interface SearchableUploadResultRow {
    id?: string | number;
    documentId?: string | number;
    DocumentId?: string | number;
    number?: string | number;
    supplierName?: string;
    SupplierName?: string;
    vendorName?: string;
    VendorName?: string;
    supplier_name?: string;
    type?: string;
    Type?: string;
    status?: string;
    Status?: string;
}

interface SearchableUploadResultsResponse {
    rows?: Array<Record<string, unknown>>;
    pagination?: {
        items?: Array<Record<string, unknown>>;
    };
}

@Injectable({
    providedIn: 'root',
})
export class TopNavDeepSearchService {
    private readonly minSearchLength = 2;
    private readonly maxResults = 10;
    private readonly defaultPageSize = 50;

    constructor(
        private readonly documentRegisterService: DocumentRegisterService,
        private readonly getSessionService: GetSessionService,
        private readonly uploadResultsService: UploadResultsService
    ) {}

    search(term: string): Observable<TopNavDeepSearchResult[]> {
        const normalizedTerm = this.normalizeTerm(term);
        if (normalizedTerm.length < this.minSearchLength) {
            return of([]);
        }

        return forkJoin({
            documents: this.searchDocuments(normalizedTerm).pipe(catchError(() => of([]))),
            sessions: this.searchSessions(normalizedTerm).pipe(catchError(() => of([]))),
            results: this.searchExtractResults(normalizedTerm).pipe(catchError(() => of([]))),
        }).pipe(
            map(({ documents, sessions, results }) =>
                [...documents, ...sessions, ...results].slice(0, this.maxResults)
            )
        );
    }

    private searchDocuments(term: string): Observable<TopNavDeepSearchResult[]> {
        return this.documentRegisterService
            .getDocumentsForRegisterPage(1, this.defaultPageSize)
            .pipe(
                map((paged) => (paged.items ?? []) as SearchableDocumentRow[]),
                map((items) =>
                    items
                        .map((row) => this.mapDocumentMatch(row, term))
                        .filter((value): value is TopNavDeepSearchResult => value !== null)
                )
            );
    }

    private mapDocumentMatch(
        row: SearchableDocumentRow,
        term: string
    ): TopNavDeepSearchResult | null {
        const docId = this.toText(row.docId ?? row.documentId ?? row.id);
        const vendorName = this.firstText(row.supplierName, row.vendorName);
        const runId = this.firstText(row.runId, row.runIdShort);
        const status = this.toText(row.status);
        const type = this.firstText(row.documentType, row.type);

        const matched = this.pickMatchedField(term, [
            { field: 'Vendor Name', value: vendorName },
            { field: 'Run ID', value: runId },
            { field: 'Status', value: status },
            { field: 'Document ID', value: docId },
            { field: 'Type', value: type },
        ]);

        if (!matched) {
            return null;
        }

        const title = vendorName || docId || 'Document';
        const subtitle = this.buildSubtitle([
            `Document ID: ${docId || '-'}`,
            `Status: ${status || '-'}`,
            `Type: ${type || '-'}`,
        ]);

        return {
            id: `document:${docId || title}`,
            title,
            subtitle,
            entityType: 'document',
            sourcePage: 'Documents Registry',
            route: `/${ROUTE_ROOTS.PLATFORM}/registerdocuments`,
            matchedField: matched.field,
            matchedValue: matched.value,
            raw: this.toRaw(row),
        };
    }

    private searchSessions(term: string): Observable<TopNavDeepSearchResult[]> {
        return this.getSessionService.getUploadSessions().pipe(
            map((items) => items as SearchableSessionRow[]),
            map((items) =>
                items
                    .map((row) => this.mapSessionMatch(row, term))
                    .filter((value): value is TopNavDeepSearchResult => value !== null)
            )
        );
    }

    private mapSessionMatch(
        row: SearchableSessionRow,
        term: string
    ): TopNavDeepSearchResult | null {
        const sessionId = this.toText(row.id);
        const status = this.toText(row.status);

        const matched = this.pickMatchedField(term, [
            { field: 'Session ID', value: sessionId },
            { field: 'Status', value: status },
        ]);

        if (!matched) {
            return null;
        }

        return {
            id: `session:${sessionId}`,
            title: sessionId || 'Session',
            subtitle: this.buildSubtitle([`Status: ${status || '-'}`]),
            entityType: 'session',
            sourcePage: 'Sessions',
            route: `/${ROUTE_ROOTS.PLATFORM}/upload`,
            matchedField: matched.field,
            matchedValue: matched.value,
            raw: this.toRaw(row),
        };
    }

    private searchExtractResults(term: string): Observable<TopNavDeepSearchResult[]> {
        // Current endpoint is paginated; this pass searches only the fetched page.
        return this.uploadResultsService.getUploadResults(1, this.defaultPageSize).pipe(
            map((response) => this.extractUploadResultRows(response as SearchableUploadResultsResponse)),
            map((rows) =>
                rows
                    .map((row) => this.mapExtractResultMatch(row, term))
                    .filter((value): value is TopNavDeepSearchResult => value !== null)
            )
        );
    }

    private extractUploadResultRows(
        response: SearchableUploadResultsResponse
    ): SearchableUploadResultRow[] {
        const pagedRows = response.pagination?.items ?? [];
        const fallbackRows = response.rows ?? [];
        return (pagedRows.length ? pagedRows : fallbackRows) as SearchableUploadResultRow[];
    }

    private mapExtractResultMatch(
        row: SearchableUploadResultRow,
        term: string
    ): TopNavDeepSearchResult | null {
        const supplierName = this.firstText(
            row.supplierName,
            row.SupplierName,
            row.vendorName,
            row.VendorName,
            row.supplier_name
        );
        const documentId = this.firstText(
            this.toText(row.documentId),
            this.toText(row.DocumentId),
            this.toText(row.number),
            this.toText(row.id)
        );
        const type = this.firstText(row.type, row.Type);
        const status = this.firstText(row.status, row.Status);

        const matched = this.pickMatchedField(term, [
            { field: 'Supplier Name', value: supplierName },
            { field: 'Document ID', value: documentId },
            { field: 'Type', value: type },
            { field: 'Status', value: status },
        ]);

        if (!matched) {
            return null;
        }

        const title = supplierName || documentId || 'Extract Result';
        const subtitle = this.buildSubtitle([
            `Document ID: ${documentId || '-'}`,
            `Type: ${type || '-'}`,
            `Status: ${status || '-'}`,
        ]);

        return {
            id: `result:${documentId || title}`,
            title,
            subtitle,
            entityType: 'result',
            sourcePage: 'Extract Results',
            route: `/${ROUTE_ROOTS.PLATFORM}/upload-results`,
            matchedField: matched.field,
            matchedValue: matched.value,
            raw: this.toRaw(row),
        };
    }

    private pickMatchedField(
        term: string,
        candidates: Array<{ field: TopNavDeepSearchMatchedField; value: string }>
    ): { field: TopNavDeepSearchMatchedField; value: string } | null {
        const match = candidates.find((item) => this.containsTerm(item.value, term));
        return match ?? null;
    }

    private normalizeTerm(value: string): string {
        return (value || '').trim().toLowerCase();
    }

    private containsTerm(value: string, term: string): boolean {
        return this.normalizeTerm(value).includes(term);
    }

    private toText(value: unknown): string {
        if (value === null || value === undefined) {
            return '';
        }
        return String(value).trim();
    }

    private firstText(...values: Array<unknown>): string {
        for (const value of values) {
            const next = this.toText(value);
            if (next) {
                return next;
            }
        }
        return '';
    }

    private buildSubtitle(parts: string[]): string {
        return parts.filter((part) => part.trim().length > 0).join(' | ');
    }

    private toRaw(value: object): Record<string, unknown> {
        return value as Record<string, unknown>;
    }
}

